import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  SupplierWithCategory,
  SupplierCategory,
  SupplierContact,
  SupplierFormData,
  SupplierUpdateData,
  SupplierCategoryFormData,
  SupplierContactFormData,
  SupplierContactUpdateData,
  SupplierFilters,
} from '../types'

const QUERY_KEY = 'suppliers'
const CATEGORIES_QUERY_KEY = 'supplier_categories'
const CONTACTS_QUERY_KEY = 'supplier_contacts'

/**
 * Hook pour récupérer la liste des fournisseurs d'une agence
 */
export function useSuppliers(filters: SupplierFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: async (): Promise<SupplierWithCategory[]> => {
      let query = supabase
        .from('suppliers')
        .select(`
          *,
          supplier_categories(*)
        `)
        .eq('agency_id', filters.agency_id)
        .order('name')

      if (filters.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active)
      }

      if (filters.category_id) {
        query = query.eq('category_id', filters.category_id)
      }

      if (filters.search) {
        query = query.or(
          `name.ilike.%${filters.search}%,code.ilike.%${filters.search}%,siret.ilike.%${filters.search}%`
        )
      }

      const { data, error } = await query
      if (error) throw error
      return data as SupplierWithCategory[]
    },
    enabled: !!filters.agency_id,
  })
}

/**
 * Hook pour récupérer le détail d'un fournisseur
 */
export function useSupplier(supplierId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', supplierId],
    queryFn: async (): Promise<SupplierWithCategory> => {
      const { data, error } = await supabase
        .from('suppliers')
        .select(`
          *,
          supplier_categories(*),
          supplier_contacts(*)
        `)
        .eq('id', supplierId!)
        .single()

      if (error) throw error
      return data as SupplierWithCategory
    },
    enabled: !!supplierId,
  })
}

/**
 * Hook pour créer un fournisseur
 */
export function useCreateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: SupplierFormData) => {
      const { data, error } = await supabase
        .from('suppliers')
        .insert({ ...formData, code: '' }) // code auto-généré par trigger
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour mettre à jour un fournisseur
 */
export function useUpdateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SupplierUpdateData }) => {
      const { data: result, error } = await supabase
        .from('suppliers')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour supprimer un fournisseur
 */
export function useDeleteSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('suppliers').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// ===== CATÉGORIES FOURNISSEURS =====

/**
 * Hook pour récupérer les catégories de fournisseurs d'une agence
 */
export function useSupplierCategories(agencyId?: string) {
  return useQuery({
    queryKey: [CATEGORIES_QUERY_KEY, agencyId],
    queryFn: async (): Promise<SupplierCategory[]> => {
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('*')
        .eq('agency_id', agencyId!)
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      return data as SupplierCategory[]
    },
    enabled: !!agencyId,
  })
}

/**
 * Hook pour créer une catégorie de fournisseur
 */
export function useCreateSupplierCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: SupplierCategoryFormData) => {
      const { data, error } = await supabase
        .from('supplier_categories')
        .insert(formData)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY, variables.agency_id] })
    },
  })
}

/**
 * Hook pour mettre à jour une catégorie
 */
export function useUpdateSupplierCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; agencyId: string; data: Partial<SupplierCategoryFormData> }) => {
      const { data: result, error } = await supabase
        .from('supplier_categories')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY, variables.agencyId] })
    },
  })
}

// ===== CONTACTS FOURNISSEUR =====

/**
 * Hook pour récupérer les contacts d'un fournisseur
 */
export function useSupplierContacts(supplierId?: string) {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEY, supplierId],
    queryFn: async (): Promise<SupplierContact[]> => {
      const { data, error } = await supabase
        .from('supplier_contacts')
        .select('*')
        .eq('supplier_id', supplierId!)
        .order('is_primary', { ascending: false })
        .order('last_name')

      if (error) throw error
      return data as SupplierContact[]
    },
    enabled: !!supplierId,
  })
}

/**
 * Hook pour créer un contact fournisseur
 */
export function useCreateSupplierContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: SupplierContactFormData) => {
      const { data, error } = await supabase
        .from('supplier_contacts')
        .insert(formData)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY, variables.supplier_id] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', variables.supplier_id] })
    },
  })
}

/**
 * Hook pour mettre à jour un contact
 */
export function useUpdateSupplierContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      supplierId: string
      data: SupplierContactUpdateData
    }) => {
      const { data: result, error } = await supabase
        .from('supplier_contacts')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY, variables.supplierId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', variables.supplierId] })
    },
  })
}

/**
 * Hook pour supprimer un contact
 */
export function useDeleteSupplierContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, supplierId }: { id: string; supplierId: string }) => {
      const { error } = await supabase.from('supplier_contacts').delete().eq('id', id)
      if (error) throw error
      return supplierId
    },
    onSuccess: (supplierId) => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY, supplierId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', supplierId] })
    },
  })
}
